<script>
  import { onMount } from 'svelte';
  import { API_BASE_URL, isAdminUser } from '../config.js';
  import LoadingSpinner from '../components/common/LoadingSpinner.svelte';
  import Navigation from '../components/layout/Navigation.svelte';
  import AgGridSvelte from 'ag-grid-svelte';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';

  export let setPage;
  export let user;
  export let onLogout = null;

  // 권한 확인 (AdminUsers.svelte 방식)
  let isAdmin = false;
  let isCompany = false;

  // 사용자 정보 초기화 (AdminUsers.svelte 방식)
  if (typeof window !== 'undefined') {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        isAdmin = isAdminUser(user);
        isCompany = user.role === 'company';
        console.log('사용자 정보:', { email: user.email, role: user.role, isAdmin, isCompany });
      } catch (e) {
        console.error('사용자 정보 파싱 오류:', e);
      }
    } else {
      console.warn('localStorage에 사용자 정보가 없습니다.');
    }
  }

  let codeGroups = [];
  let selectedGroup = null;
  let codeDetails = [];
  let loading = false;
  let searchText = '';
  
  // AG Grid 설정
  let gridApi;
  let columnDefs = [
    { headerName: '코드 값', field: 'code_value', width: 150, pinned: 'left', resizable: true },
    { headerName: '코드 이름', field: 'code_name', flex: 1, resizable: true },
    { headerName: '사용여부', field: 'use_yn', width: 100, cellRenderer: statusRenderer, resizable: true },
    { headerName: '정렬순서', field: 'sort_order', width: 100, resizable: true },
    { headerName: '등록일', field: 'created_at', width: 120, cellRenderer: dateRenderer, resizable: true },
    { headerName: '수정일', field: 'updated_at', width: 120, cellRenderer: dateRenderer, resizable: true }
  ];

  function statusRenderer(params) {
    const status = params.value ? '사용중' : '사용안함';
    const className = params.value ? 'status-active' : 'status-inactive';
    return `<span class="${className}">${status}</span>`;
  }

  function dateRenderer(params) {
    if (!params.value) return '';
    return new Date(params.value).toLocaleDateString('ko-KR');
  }

  // 검색 필터 함수
  function onSearchChanged() {
    if (gridApi) {
      gridApi.setQuickFilter(searchText);
    }
  }


  onMount(() => {
    console.log('CommonCodes 페이지 마운트, 권한 체크:', { isAdmin, user: user?.email });
    
    if (!isAdmin) {
      console.warn('관리자 권한이 없습니다. 홈으로 이동합니다.');
      alert('관리자 권한이 필요합니다.');
      setPage('home');
      return;
    }
    
    console.log('관리자 권한 확인됨. 코드 그룹 로드를 시작합니다.');
    loadCodeGroups();
  });

  // 공통 API 응답 처리 함수 (AdminUsers.svelte 방식)
  function handleApiResponse(response) {
    if (response.status === 401 || response.status === 403) {
      console.warn('인증 실패. 로그인 페이지로 이동합니다.');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      alert('세션이 만료되었습니다. 다시 로그인해주세요.');
      window.location.href = '/?t=' + Date.now() + '#login';
      return false;
    }
    return true;
  }


  // 코드 그룹 목록 로드 (AdminUsers.svelte 방식으로 수정)
  async function loadCodeGroups() {
    try {
      loading = true;
      console.log('코드 그룹 목록 로드 시작...');
      
      const token = localStorage.getItem('token');
      console.log('토큰 존재 여부:', !!token);
      
      if (!token) {
        console.warn('토큰이 없습니다. 로그인 페이지로 이동합니다.');
        setPage('login');
        return;
      }

      const endpoint = `${API_BASE_URL}/api/codes/groups`;
      console.log('API 호출:', endpoint);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        // 공통 인증 오류 처리 (AdminUsers.svelte 방식)
        if (!handleApiResponse(response)) return;
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || '코드 그룹 목록을 불러오지 못했습니다.');
      }

      const data = await response.json();
      console.log('서버 응답 데이터:', data);
      
      // 서버 응답 구조에 맞게 수정
      codeGroups = data.data || [];
      console.log('로드된 코드 그룹 개수:', codeGroups.length);
      
      if (codeGroups.length > 0 && !selectedGroup) {
        console.log('첫 번째 그룹 자동 선택:', codeGroups[0]);
        selectGroup(codeGroups[0]);
      }
    } catch (error) {
      console.error('코드 그룹 목록 로드 실패:', error);
      alert(error.message || '코드 그룹 목록을 불러오는데 실패했습니다.');
    } finally {
      loading = false;
    }
  }

  // 특정 그룹의 상세 코드 로드 (AdminUsers.svelte 방식으로 수정)
  async function loadCodeDetails(groupId) {
    try {
      loading = true;
      console.log('상세 코드 로드 시작, 그룹 ID:', groupId);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('토큰이 없습니다. 로그인 페이지로 이동합니다.');
        setPage('login');
        return;
      }

      const endpoint = `${API_BASE_URL}/api/codes/details/group/${groupId}`;
      console.log('API 호출:', endpoint);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('상세 코드 응답 상태:', response.status, response.statusText);

      if (!response.ok) {
        if (!handleApiResponse(response)) return;
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || '상세 코드 목록을 불러오지 못했습니다.');
      }

      const data = await response.json();
      console.log('상세 코드 응답 데이터:', data);
      
      codeDetails = data.data || [];
      console.log('로드된 상세 코드 개수:', codeDetails.length);
    } catch (error) {
      console.error('상세 코드 목록 로드 실패:', error);
      alert(error.message || '상세 코드 목록을 불러오는데 실패했습니다.');
    } finally {
      loading = false;
    }
  }

  // 그룹 선택
  function selectGroup(group) {
    selectedGroup = group;
    loadCodeDetails(group.id);
  }


</script>

<!-- 상단 네비게이션 -->
<Navigation 
  {user}
  currentPage="common-codes"
  {onLogout}
/>

<div class="common-codes-container">
  <div class="page-header">
    <h2>공통 코드 관리</h2>
  </div>

  {#if loading}
    <LoadingSpinner />
  {:else}
    <div class="content-grid">
      <!-- 코드 그룹 목록 -->
      <div class="groups-panel">
        <div class="panel-header">
          <h3>코드 그룹 목록</h3>
        </div>
        <div class="groups-list">
          {#each codeGroups as group}
            <div 
              class="group-item"
              class:selected={selectedGroup?.id === group.id}
              on:click={() => selectGroup(group)}
            >
              <div class="group-info">
                <div class="group-code">{group.group_code}</div>
                <div class="group-name">{group.group_name}</div>
                <div class="group-meta">
                  <span class="use-status" class:active={group.use_yn}>
                    {group.use_yn ? '사용중' : '사용안함'}
                  </span>
                  <span class="sort-order">순서: {group.sort_order}</span>
                </div>
              </div>
            </div>
          {/each}
          {#if codeGroups.length === 0}
            <div class="empty-state">
              <p>등록된 코드 그룹이 없습니다.</p>
              <p><small>코드 그룹을 추가하려면 우상단의 "코드 그룹 추가" 버튼을 클릭하세요.</small></p>
            </div>
          {/if}
        </div>
      </div>

      <!-- 상세 코드 목록 -->
      <div class="details-panel">
        <div class="panel-header">
          <h3>
            상세 코드 목록
            {#if selectedGroup}
              <span class="selected-group">({selectedGroup.group_code} - {selectedGroup.group_name})</span>
            {/if}
          </h3>
          {#if selectedGroup}
            <div class="search-container">
              <input
                type="text"
                placeholder="검색..."
                bind:value={searchText}
                on:input={onSearchChanged}
                class="search-input"
              />
              <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
              </svg>
            </div>
          {/if}
        </div>
        
        {#if selectedGroup}
          <div class="ag-theme-alpine details-grid">
            <AgGridSvelte
              bind:api={gridApi}
              {columnDefs}
              rowData={codeDetails}
              domLayout={'normal'}
              headerHeight={40}
              rowHeight={50}
              suppressRowClickSelection={true}
              animateRows={true}
              pagination={true}
              paginationPageSize={20}
              suppressCellFocus={true}
              enableColResize={true}
              sizeColumnsToFit={true}
            />
          </div>
        {:else}
          <div class="empty-state">
            <p>코드 그룹을 선택해주세요.</p>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>


<style>
  .common-codes-container {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .page-header {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #e9ecef;
  }

  .page-header h2 {
    color: #333;
    font-size: 1.8rem;
    font-weight: 600;
    margin: 0;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 30px;
    min-height: 600px;
  }

  .groups-panel, .details-panel {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
  }

  .panel-header h3 {
    margin: 0;
    color: #333;
    font-size: 1.2rem;
    font-weight: 600;
  }

  .selected-group {
    color: #3273dc;
    font-size: 1rem;
    font-weight: 500;
  }

  .search-container {
    position: relative;
    width: 250px;
  }

  .search-input {
    width: 100%;
    padding: 8px 35px 8px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
  }

  .search-input:focus {
    border-color: #3273dc;
    box-shadow: 0 0 0 2px rgba(50, 115, 220, 0.1);
  }

  .search-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: #666;
    pointer-events: none;
  }

  .groups-list {
    max-height: 350px;
    overflow-y: auto;
  }

  .details-grid {
    height: 600px;
    width: 100%;
  }

  .group-item {
    padding: 15px 20px;
    border-bottom: 1px solid #f0f0f0;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .group-item:hover {
    background: #f8f9fa;
  }

  .group-item.selected {
    background: #e3f2fd;
    border-left: 4px solid #3273dc;
  }

  .group-code {
    font-weight: 600;
    color: #333;
    font-size: 1.1rem;
    margin-bottom: 5px;
  }

  .group-name {
    color: #333;
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .group-meta {
    display: flex;
    gap: 15px;
    align-items: center;
  }

  .use-status {
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
    background: #f8d7da;
    color: #721c24;
  }

  .use-status.active {
    background: #d4edda;
    color: #155724;
  }

  .sort-order {
    font-size: 0.8rem;
    color: #868e96;
  }


  .empty-state {
    padding: 40px 20px;
    text-align: center;
    color: #868e96;
  }

  .empty-state p {
    margin: 0;
    font-size: 1rem;
  }

  /* 반응형 */
  @media (max-width: 1024px) {
    .content-grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  @media (max-width: 768px) {
    .common-codes-container {
      padding: 15px;
    }

    .page-header {
      flex-direction: column;
      gap: 15px;
      align-items: flex-start;
    }

    .panel-header {
      flex-direction: column;
      gap: 15px;
      align-items: flex-start;
    }

    .group-item {
      gap: 15px;
    }
  }

  /* AG Grid 커스텀 스타일 */
  :global(.details-grid .ag-theme-alpine) {
    font-family: inherit;
  }

  :global(.details-grid .ag-header) {
    background: #f8f9fa;
    border-bottom: 2px solid #e9ecef;
  }

  :global(.details-grid .ag-header-cell) {
    border-right: 1px solid #e9ecef;
    font-weight: 600;
    color: #333;
  }

  :global(.details-grid .ag-row) {
    border-bottom: 1px solid #f0f0f0;
  }

  :global(.details-grid .ag-row:hover) {
    background: #f8f9fa;
  }

  :global(.details-grid .status-active) {
    background: #d4edda;
    color: #155724;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
  }

  :global(.details-grid .status-inactive) {
    background: #f8d7da;
    color: #721c24;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
  }
</style>